import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { readScheduleRows } from '../../realtime/useScheduleRealtime';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Container, Spinner, Tabs, Tab, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import TopNav from "../TopNav/TopNav";
import NPWeeklyScheduleTable from "./NPWeeklyScheduleTable";
import CsmEnclosureTable from "./CsmEnclosureTable";
import ShippedEnclosuresTable from "./ShippedEnclosuresTable";
import NPJobs from "./NPJobs";
import { usePermissions } from "../../hooks/usePermissions";
import useCriticalData from "../../functions/useCriticalData";
import { readNpSnapshot } from '../../redux/api/npSnapshot';
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import {
  PRODUCTION_NORTH_PRAIRIE_TAB_ROUTES,
  getProductionNorthPrairiePathForTab,
  getProductionNorthPrairieTabFromPath,
} from "../../config/productionRoutes";

const parseEnhancedSection = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const toSectionArray = (value) => {
  const parsedValue = parseEnhancedSection(value);
  if (!parsedValue) {
    return [];
  }

  return Array.isArray(parsedValue) ? parsedValue : [parsedValue];
};

const normalizeEnhancedRows = (response) => {
  const parsedResponse = parseEnhancedSection(response);

  if (Array.isArray(parsedResponse)) {
    return parsedResponse;
  }

  if (!parsedResponse || typeof parsedResponse !== "object") {
    return [];
  }

  for (const key of ["data", "items", "records", "result", "results"]) {
    if (parsedResponse[key]) {
      const nestedRows = normalizeEnhancedRows(parsedResponse[key]);
      if (nestedRows.length > 0) {
        return nestedRows;
      }
    }
  }

  if (parsedResponse.GeneralProjectInfo) {
    return [parsedResponse];
  }

  return Object.values(parsedResponse).filter(
    (record) => record && typeof record === "object" && record.GeneralProjectInfo
  );
};

function NPContainer() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const apiUrls = useSelector(selectApiUrls);
  const { canView, isLoading } = usePermissions();
  const canAccessNorthPrairie = canView("np.container");
  const canViewNpJobs = canView("user.np-jobs");
  const visibleTabs = useMemo(
    () => PRODUCTION_NORTH_PRAIRIE_TAB_ROUTES.filter(
      (tab) => !tab.permissionKey || canView(tab.permissionKey)
    ),
    [canView]
  );
  const requestedTab = getProductionNorthPrairieTabFromPath(location.pathname);
  const activeTab = requestedTab && visibleTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : visibleTabs[0]?.id || '';
  const [npJobsData, setNpJobsData] = useState({
    generalProject: [],
    fertigation: [],
    pumpManifoldPiping: [],
    hcRequirements: [],
    npSchedule: [],
  });
  const [npJobsLoading, setNpJobsLoading] = useState(false);
  const [npJobsError, setNpJobsError] = useState(null);
  const {
    initialized: criticalDataInitialized,
    errors,
  } = useCriticalData({
    apiKeys: ["dropdownValues", "holidays"],
    includeUserDropdowns: false,
    autoLoad: true,
    forceRefresh: false,
  });

  useGuardedRealtimeList({ apiKey: 'npEnhanced', enabled: canAccessNorthPrairie && canViewNpJobs && activeTab === 'hdpe',
    blocked: npJobsLoading || isLoading,
    additionalResources: ['portal:general-project-info', 'portal:fertigation', 'portal:pump-manifold-piping', 'portal:hc-requirements', 'portal:np-schedule'],
    decode: payload => { readScheduleRows(payload); return normalizeEnhancedRows(payload); },
    apply: enhancedRows => setNpJobsData({
      generalProject: enhancedRows.flatMap(row => toSectionArray(row.GeneralProjectInfo)),
      fertigation: enhancedRows.flatMap(row => toSectionArray(row.Fertigation)),
      pumpManifoldPiping: enhancedRows.flatMap(row => toSectionArray(row.PumpManifoldPiping)),
      hcRequirements: enhancedRows.flatMap(row => toSectionArray(row.HCRequirements)),
      npSchedule: enhancedRows.flatMap(row => toSectionArray(row.NPSchedule)),
    }),
  });

  const criticalDataError = errors.dropdownValues || errors.holidays || null;

  const loadNpJobsData = useCallback(async () => {
    if (!apiUrls.npEnhanced) {
      return;
    }

    setNpJobsLoading(true);
    setNpJobsError(null);

    try {
      const response = await dispatch(readNpSnapshot(apiUrls.npEnhanced)).unwrap();
      const enhancedRows = normalizeEnhancedRows(response);

      setNpJobsData({
        generalProject: enhancedRows.flatMap((row) => toSectionArray(row.GeneralProjectInfo)),
        fertigation: enhancedRows.flatMap((row) => toSectionArray(row.Fertigation)),
        pumpManifoldPiping: enhancedRows.flatMap((row) => toSectionArray(row.PumpManifoldPiping)),
        hcRequirements: enhancedRows.flatMap((row) => toSectionArray(row.HCRequirements)),
        npSchedule: enhancedRows.flatMap((row) => toSectionArray(row.NPSchedule)),
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to load NP Jobs data";
      setNpJobsError(errorMessage);
    } finally {
      setNpJobsLoading(false);
    }
  }, [apiUrls.npEnhanced, dispatch]);

  useEffect(() => {
    if (isLoading || !canAccessNorthPrairie || !canViewNpJobs || activeTab !== 'hdpe') {
      return;
    }

    loadNpJobsData();
  }, [isLoading, canAccessNorthPrairie, canViewNpJobs, activeTab, loadNpJobsData]);

  useEffect(() => {
    if (isLoading || !canAccessNorthPrairie || visibleTabs.length === 0 || !activeTab) {
      return;
    }

    const canonicalPath = getProductionNorthPrairiePathForTab(activeTab);
    const currentPath = location.pathname.replace(/\/+$/, '').toLowerCase();
    if (canonicalPath && currentPath !== canonicalPath.toLowerCase()) {
      navigate(canonicalPath, { replace: true });
    }
  }, [
    activeTab,
    canAccessNorthPrairie,
    isLoading,
    location.pathname,
    navigate,
    visibleTabs.length,
  ]);

  const renderTabContent = (tabId) => {
    switch (tabId) {
      case "csm":
        return <NPWeeklyScheduleTable />;
      case "enclosures":
        return (
          <Tabs defaultActiveKey="status" id="enclosure-view-tabs" className="mb-3" mountOnEnter unmountOnExit>
            <Tab eventKey="status" title="Status">
              <CsmEnclosureTable />
            </Tab>
            <Tab eventKey="due-dates" title="Due Dates">
              <CsmEnclosureTable showDueDates />
            </Tab>
          </Tabs>
        );
      case "shipped-enclosures":
        return <ShippedEnclosuresTable />;
      case "hdpe":
        return (
          <NPJobs
            apiData={npJobsData}
            loading={npJobsLoading}
            error={npJobsError}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading || (!criticalDataInitialized && !criticalDataError)) {
    return (
      <>
        <TopNav />
        <Container className="mt-4 text-center">
          <Spinner animation="border" />
        </Container>
      </>
    );
  }

  if (!canAccessNorthPrairie) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <TopNav />
      <Container fluid className="mt-3">
        {criticalDataError && (
          <Alert variant="danger" className="mb-3">
            Failed to load North Prairie dropdown data.
          </Alert>
        )}
        <div className="mb-2">
          <h2>North Prairie</h2>
          <p className="text-muted mb-0">
            Upcoming North Prairie components will be managed here.
          </p>
        </div>

        <Tabs
          mountOnEnter
          unmountOnExit
          activeKey={activeTab}
          onSelect={(tabId) => {
            const path = getProductionNorthPrairiePathForTab(tabId);
            if (path) navigate(path);
          }}
          className="mb-3"
          id="north-prairie-tabs"
        >
          {visibleTabs.map((tab) => (
            <Tab eventKey={tab.id} title={tab.label} key={tab.id}>
              {renderTabContent(tab.id)}
            </Tab>
          ))}
        </Tabs>
      </Container>
    </>
  );
}

export default NPContainer;
